import { gql, useMutation } from '@apollo/client';
import { Modal, useToast } from '@chakra-ui/react';
import * as React from 'react';

import { UserCsvUploadOperator } from '@/graphql-types/globalTypes';
import { useMemberManagement } from '@/hooks/useMemberManagement';
import { useUploadFileToS3 } from '@/hooks/useUploadFileToS3';
import { logError } from '@/imports/logging/ClientLogger';
import {
  MembersCsvImportModal_ExecuteUserCsvUpload,
  MembersCsvImportModal_ExecuteUserCsvUploadVariables,
} from '@/imports/ui/admin/components/people/graphql-types/MembersCsvImportModal_ExecuteUserCsvUpload';
import { ImportTypeOption } from '@/imports/ui/components/csv/CsvImportConfiguration';
import { CsvImportModalFrameEnhanced } from '@/imports/ui/components/csv/CsvImportModalFrameEnhanced';
import { useCsvImportForeignKeyConfig } from '@/imports/ui/components/csv/useCsvImportConfig';

import { FieldValidationError } from '../../../../../client/legacy-shared-modules/csv/csv-field-mapping';
import {
  ValidationFn,
  getValidateCsvFileHeaders,
  normalizeHeader,
} from '../../../../../client/legacy-shared-modules/csv/helpers';
import { SuccessModal } from '../SuccessModal';
import { usePrepareUserCsv } from './usePrepareUserCsv';
import { getUserUploadTemplate, REQUIRED_USER_HEADERS_FOR_INSERT, UserCsvHeaders } from './util';

const FOREIGN_KEY_OPTIONS: {
  value: string;
  label: UserCsvHeaders;
}[] = [
  {
    value: 'username',
    label: UserCsvHeaders.USERNAME,
  },
  {
    value: 'emails',
    label: UserCsvHeaders.EMAIL,
  },
  {
    value: 'integrationId',
    label: UserCsvHeaders.EXTERNAL_ID,
  },
];

const EXECUTE_USER_CSV_UPLOAD = gql`
  mutation MembersCsvImportModal_ExecuteUserCsvUpload(
    $input: ExecuteUserCsvUploadInput!
  ) {
    executeUserCsvUpload(input: $input) {
      csvUploadId
    }
  }
`;

type IMembersCsvImportModalProps = {
  open: boolean;
  onClose: () => void;
};

export const MembersCsvImportModal: React.FC<IMembersCsvImportModalProps> = (
  props
) => {
  const [uploadCsvData, { loading: uploadMutationLoading }] = useMutation<
    MembersCsvImportModal_ExecuteUserCsvUpload,
    MembersCsvImportModal_ExecuteUserCsvUploadVariables
  >(EXECUTE_USER_CSV_UPLOAD);

  const [csvFile, setCsvFile] = React.useState<File | null>(null);
  const [validationErrors, setValidationErrors] =
    React.useState<FieldValidationError | null>(null);
  const [shouldSendEmailInvites, setShouldSendEmailInvites] =
    React.useState(false);
  const [showSuccessModal, setShowSuccessModal] =
    React.useState<boolean>(false);
  const [uploadedCsv, setUploadedCsv] = React.useState<boolean>(false);
  const toast = useToast();

  const { isActive: memberManagementEnabled } = useMemberManagement();

  const {
    prepareUserCsv,
    prepareUserCsvData,
    prepareUserCsvLoading,
    clearCsvResult,
  } = usePrepareUserCsv();

  const [uploadFileToS3, { isUploading, error: uploadError, isCanceled }] =
    useUploadFileToS3();

  const prepUserCsvUpload = prepareUserCsvData?.prepUserCsvUpload;

  const handleUploadFileToS3 = React.useCallback(
    async (file: File): Promise<void> => {
      try {
        if (
          prepUserCsvUpload?.metadataHeaders &&
          prepUserCsvUpload?.presignedS3Url
        ) {
          const newMetadataHeaders = prepUserCsvUpload.metadataHeaders.reduce(
            (obj: Record<string, string>, { name, value }) => (
              (obj[name] = value), obj
            ),
            {}
          );

          await uploadFileToS3({
            file,
            presignedS3Url: prepUserCsvUpload.presignedS3Url,
            metadataHeaders: newMetadataHeaders,
          });
        } else {
          // eslint-disable-next-line quotes
          logError(new Error("Can't upload csv file with failed prep"));
        }
      } catch (err) {
        toast({
          title: 'Failed to upload csv. Please try again.',
          status: 'error',
        });
        logError(err);
      }
    },
    [toast, uploadFileToS3, prepUserCsvUpload]
  );

  React.useEffect(() => {
    if (prepareUserCsvData && !uploadedCsv && !!csvFile) {
      setUploadedCsv(true);
      handleUploadFileToS3(csvFile).catch((err) => logError(err));
    }
  }, [
    prepareUserCsvData,
    setUploadedCsv,
    handleUploadFileToS3,
    csvFile,
    uploadedCsv,
  ]);

  const handleExecuteUserCsvUpload = async (
    csvUploadId: string,
    operator: string,
    sendInviteEmailsOnInsert: boolean,
    matchField: string
  ): Promise<void> => {
    try {
      await uploadCsvData({
        variables: {
          input: {
            csvUploadId,
            operator: operator as UserCsvUploadOperator,
            sendInviteEmailsOnInsert,
            matchField,
          },
        },
      });
      setShowSuccessModal(true);
    } catch (err) {
      toast({
        title: 'Failed to execute user csv upload. Please try again.',
        status: 'error',
      });
      logError(err);
    }
  };

  const getValidateFileFn = (
    importType: string,
    foreignKey: string
  ): ((file: File) => Promise<void>) => {
    const validateHeaders: ValidationFn = (headers) => {
      const matchingLabel = FOREIGN_KEY_OPTIONS.filter(
        (option) => option.value === foreignKey
      )[0].label;

      if (importType === UserCsvUploadOperator.INSERT) {
        const errors = REQUIRED_USER_HEADERS_FOR_INSERT.filter(
          (requiredHeader) => !headers.includes(normalizeHeader(requiredHeader))
        ).map((missingField) => `Missing required field: ${missingField}`);

        if (!headers.includes(normalizeHeader(matchingLabel))) {
          errors.push(`Missing match field: ${matchingLabel}`);
        }
        if (errors.length > 0) {
          return { errors };
        }
      } else {
        if (!headers.includes(normalizeHeader(matchingLabel))) {
          return { errors: [`Missing match field: ${matchingLabel}`] };
        }
      }

      return null;
    };
    return async (file) => {
      await getValidateCsvFileHeaders({
        validateHeaders,
        onSuccess: (successFile) => {
          setValidationErrors(null);
          setCsvFile(successFile);
        },
        onErrors: (errors) => {
          setCsvFile(null);
          setValidationErrors(errors);
        },
      })(file);
    };
  };

  const onConfigChange = React.useCallback(
    (importType, foreignKey) => {
      if (!csvFile) {
        return;
      }
      void getValidateFileFn(importType, foreignKey)(csvFile);
    },
    [csvFile]
  );

  const { importType, onImportTypeChange, foreignKeyConfig } =
    useCsvImportForeignKeyConfig({
      defaultForeignKey: 'emails',
      defaultImportType: UserCsvUploadOperator.INSERT,
      onConfigChange,
    });

  const onClose = (): void => {
    setValidationErrors(null);
    setCsvFile(null);
    onImportTypeChange(UserCsvUploadOperator.INSERT);
    setUploadedCsv(false);
    clearCsvResult();
    props.onClose();
  };

  const handleRemoveFile = (e: React.SyntheticEvent): void => {
    e.stopPropagation();
    setValidationErrors(null);
    setCsvFile(null);
    setUploadedCsv(false);
    clearCsvResult();
  };

  const submitFileUpload = async (csvUploadId: string): Promise<void> => {
    await handleExecuteUserCsvUpload(
      csvUploadId,
      importType,
      shouldSendEmailInvites,
      foreignKeyConfig.foreignKey
    );
    onClose();
  };

  const onFileChange = async (file: File | null): Promise<void> => {
    clearCsvResult();
    await prepareUserCsv();
    if (!!file) {
      const validateFunc = getValidateFileFn(
        importType,
        foreignKeyConfig.foreignKey
      );
      await validateFunc(file);
    }
  };

  const importTypeOptions: ImportTypeOption[] = [
    { value: UserCsvUploadOperator.INSERT, label: 'Insert' },
    { value: UserCsvUploadOperator.UPDATE, label: 'Update' },
  ];

  const hasLoadingSideEffect =
    isUploading || prepareUserCsvLoading || uploadMutationLoading;

  const isDisabled =
    isCanceled ||
    !!uploadError ||
    hasLoadingSideEffect ||
    !!(validationErrors && validationErrors.errors) ||
    !csvFile;

  const membersCsvImport = (
    <CsvImportModalFrameEnhanced
      pluralEntityName="Individual"
      footerProps={{
        primaryAction: {
          onClick: async () => {
            if (prepareUserCsvData?.prepUserCsvUpload.csvUploadId) {
              await submitFileUpload(
                prepareUserCsvData.prepUserCsvUpload.csvUploadId
              );
            } else {
              logError(new Error('missing csvUploadId'));
            }
          },
          content: 'Upload',
          loading: hasLoadingSideEffect,
          disabled: isDisabled,
        },
      }}
      template={getUserUploadTemplate(memberManagementEnabled)}
      file={csvFile}
      onFileChange={onFileChange}
      onRemoveFile={handleRemoveFile}
      validationErrors={validationErrors}
      importConfig={{
        importType,
        importTypeOptions,
        onImportTypeChange,
        foreignKeyConfig: {
          foreignKeyOptions: FOREIGN_KEY_OPTIONS,
          onForeignKeyChange: foreignKeyConfig.onForeignKeyChange,
          foreignKey: foreignKeyConfig.foreignKey,
        },
      }}
      hasLoadingSideEffect={hasLoadingSideEffect}
      sendEmailInvitesProps={{
        shouldSendEmailInvites,
        setShouldSendEmailInvites,
      }}
    />
  );

  if (showSuccessModal) {
    return (
      <SuccessModal
        isOpen={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
      />
    );
  }

  return (
    <Modal isOpen={props.open} onClose={onClose} size="xl">
      {membersCsvImport}
    </Modal>
  );
};
